import { useEffect, useRef, type ImgHTMLAttributes } from 'react';
import type { MediaRef } from '@cng/shared';

type MediaLike = (MediaRef & {
  derivatives?: { name: string; width: number; url: string; format: string }[];
  mimeType?: string;
}) | null | undefined;

interface SmartImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  media: MediaLike;
  alt?: string;
  /** Overrides the media's own URL, for cases where only a raw path is known. */
  src?: string;
}

/**
 * Renders a media asset with the WebP derivatives generated at upload time, so
 * a phone downloads a 400px file instead of the 1600px original. Falls back to
 * a plain <img> whenever derivatives are absent.
 */
export function SmartImage({
  media, alt, src, sizes, loading = 'lazy', decoding = 'async', ...rest
}: SmartImageProps) {
  const url = src ?? media?.url ?? '';
  if (!url) return null;

  const altText = alt ?? media?.alt ?? '';
  const derivatives = (media?.derivatives ?? [])
    .filter((d) => d.format === 'webp' && d.url)
    .sort((a, b) => a.width - b.width);

  const img = (
    <img
      src={url}
      alt={altText}
      width={media?.width ?? undefined}
      height={media?.height ?? undefined}
      loading={loading}
      decoding={decoding}
      sizes={sizes}
      {...rest}
    />
  );

  if (derivatives.length === 0) return img;

  const srcSet = derivatives.map((d) => `${d.url} ${d.width}w`).join(', ');

  return (
    <picture>
      <source type="image/webp" srcSet={srcSet} sizes={sizes} />
      {img}
    </picture>
  );
}

interface SmartVideoProps {
  media: MediaLike;
  poster?: string | null;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  ariaLabel?: string;
}

/**
 * A video clip that costs nothing until it is about to be seen.
 *
 * The homepage carries five autoplaying clips totalling 27.5 MB. Marking them
 * `autoplay` makes the browser fetch all of it during the first paint, which on
 * a phone is most of a minute and a noticeable share of someone's data — for
 * content that sits below the fold and many visitors never reach. So the
 * attribute is left off and playback starts from an IntersectionObserver when
 * the clip approaches the viewport; `preload="none"` means not a byte moves
 * before that.
 *
 * The markup still carries a real `src`, so the clip works without JavaScript.
 */
export function SmartVideo({
  media, poster, className, autoPlay = true, loop = true, muted = true, controls = false, ariaLabel,
}: SmartVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const url = media?.url;

  useEffect(() => {
    const el = ref.current;
    if (!autoPlay || !el) return undefined;

    // React does not reflect `muted` onto the element, and an unmuted video is
    // refused autoplay by every browser — so set it on the node itself.
    el.muted = muted;

    const start = () => {
      el.preload = 'auto';
      const played = el.play();
      // Rejected when the tab is in the background or the codec is refused;
      // either way the poster stays and nothing breaks.
      if (played && typeof played.catch === 'function') played.catch(() => {});
    };

    if (typeof IntersectionObserver === 'undefined') {
      start();
      return undefined;
    }

    // 300px of lead time: loading begins just before the clip scrolls in, so
    // the visitor sees it playing rather than waiting for it.
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        start();
        observer.disconnect();
      }
    }, { rootMargin: '300px' });

    observer.observe(el);
    return () => observer.disconnect();
  }, [autoPlay, muted, url]);

  if (!url) return null;
  return (
    <video
      ref={ref}
      className={className}
      src={url}
      poster={poster ?? undefined}
      loop={loop}
      muted={muted}
      playsInline
      controls={controls}
      preload="none"
      aria-label={ariaLabel || media?.alt || undefined}
    />
  );
}
